import { AutoComplete, Row } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useContext, useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import { searchUserService } from "../../../infrastructure/services/UsersService";
import {
  UserDataContext,
  UiTextsContext,
  LangContext,
} from "../../contexts/Contexts";
import { hashtagPath, userPath } from "../../routes/routes";
import { AvatarLayout } from "../publications/AvatarLayout";

export const AutoCompleteSearch = ({
  blockList,
  setBlockList,
  onActiveChange,
}) => {
  const navigate = useNavigate();
  const { userData } = useContext(UserDataContext);
  const { texts } = useContext(UiTextsContext);
  const { lang } = useContext(LangContext);

  // Mismo criterio que AutoCompleteBlock: el contexto manda, y si no hay,
  // se cae al idioma del usuario o al persistido en LocalStorage.
  const isSpanish = (
    lang ||
    userData?.idioma ||
    localStorage.getItem("language") ||
    "en"
  )
    .toLowerCase()
    .startsWith("es");

  const [searching, setSearching] = useState(false);
  const [options, setOptions] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");
  const [activeLocal, setActiveLocal] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 1200 });
  const inputHasFocusRef = useRef(false);
  const wrapperRef = useRef(null);

  const placeholder = isMobile ? "" : texts?.LB0645;

  const setActive = (value) => {
    const nextActive = Boolean(value);
    setActiveLocal(nextActive);
    onActiveChange?.(nextActive);
    window.dispatchEvent(
      new CustomEvent("search:autocomplete-active", {
        detail: { active: nextActive },
      }),
    );
  };

  const onSearch = (value) => {
    setSearch(value);
    const trimmedValue = value.trim();
    setActive(Boolean(trimmedValue));
    if (!trimmedValue) {
      setOptions([]);
    }
  };

  const onFocus = () => {
    inputHasFocusRef.current = true;
    setActive(true);
  };

  const onBlur = () => {
    inputHasFocusRef.current = false;
    if (!search.trim()) {
      setActive(false);
    }
  };

  const onClearRequest = () => {
    setSearch("");
    setOptions([]);
    setActive(false);
  };

  const onSelect = (value, option) => {
    setSearch("");
    setOptions([]);
    setActive(false);

    if (option?.resultType === "hashtag" && option?.hashtag) {
      navigate(`${hashtagPath}/${encodeURIComponent(option.hashtag)}`);
      return;
    }

    if (blockList) {
      setBlockList([option]);
    }

    if (option?.userData) {
      navigate(userPath, { state: option.userData });
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(search), 500);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target?.closest?.(".nb-autocomplete-dropdown")) {
        return;
      }

      if (!wrapperRef.current?.contains(event.target)) {
        inputHasFocusRef.current = false;
        setSearch("");
        setOptions([]);
        setActive(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [search]);

  useEffect(() => {
    const searchBack = async () => {
      setSearching(true);

      try {
        const req = { Criteria: debouncedValue, IdUsuario: userData.idUsuario };
        const results = await searchUserService(req);
        const initialOpts = results.map((user) => {
          const isHashtag =
            typeof user?.nombres === "string" && user.nombres.startsWith("#");
          const hashtag = isHashtag ? user.nombres.slice(1) : "";

          return {
            value: isHashtag
              ? user.nombres
              : `${user.nombres} ${user.apellidos}`,
            key: isHashtag ? `hashtag-${user.nombres}` : user.idUsuario,
            userId: isHashtag ? undefined : user.idUsuario,
            userData: isHashtag ? undefined : user,
            resultType: isHashtag ? "hashtag" : "user",
            hashtag,
            label: isHashtag ? (
              <div style={{ padding: "8px 12px", fontWeight: 600 }}>
                {user.nombres}
              </div>
            ) : (
              <AvatarLayout
                userData={user}
                photo={user.fotoPerfilUrl || user.imagenBase64}
              />
            ),
          };
        });
        setOptions(initialOpts);
        setActive(
          Boolean(
            inputHasFocusRef.current || debouncedValue || initialOpts.length,
          ),
        );
      } catch (error) {
        console.error("Error while searching", error);
      } finally {
        setSearching(false);
      }
    };

    if (debouncedValue) {
      searchBack();
    } else {
      setOptions([]);
      setActive(false);
    }
  }, [debouncedValue, userData?.idUsuario]);

  // VIVA-920: mismo defecto que en el buscador del modal de invitación.
  const notFound = (
    <Row>
      {texts?.LB0418 ||
        (isSpanish ? "No se encontraron resultados" : "No results found")}
    </Row>
  );
  const searchingElement = (
    <Row>
      {/* Aqui NO va LB0651: esa clave significa "Publicacion guardada" y es la
          que consume SavePub. Usarla mostraba "Post saved" mientras el buscador
          consultaba. LB0597 es "Cargando" / "Loading", que si corresponde. */}
      {texts?.LB0597 || (isSpanish ? "Buscando..." : "Searching...")}
    </Row>
  );

  const getStablePopupContainer = (triggerNode) => {
    if (!triggerNode) {
      return document.body;
    }

    return (
      triggerNode.closest(
        ".new-nb-search-icons-container, .new-nb-search-container, .nbm-wrap, .searchbar-header, .searchbar-container",
      ) || document.body
    );
  };

  return (
    <div
      ref={wrapperRef}
      className={activeLocal ? "nb-search-pill-active" : "nb-search-pill"}
    >
      <AutoComplete
        onSearch={onSearch}
        onSelect={onSelect}
        onFocus={onFocus}
        onBlur={onBlur}
        options={options}
        open={Boolean(search.trim())}
        value={search}
        onChange={setSearch}
        notFoundContent={searching ? searchingElement : notFound}
        popupMatchSelectWidth={isMobile ? 250 : 780}
        dropdownMatchSelectWidth={isMobile ? 250 : 780}
        popupClassName="nb-autocomplete-dropdown"
        dropdownClassName="nb-autocomplete-dropdown"
        className="nb-autocomplete"
        getPopupContainer={getStablePopupContainer}
        dropdownStyle={{ zIndex: 1201 }}
        style={{ width: "100%" }}
        allowClear={{
          clearIcon: (
            <span className="nb-search-clear" onMouseDown={onClearRequest}>
              x
            </span>
          ),
        }}
        prefix={
          <div
            style={{
              paddingBottom: 6,
              width: 30,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
            }}
          >
            <SearchOutlined />
          </div>
        }
        placeholder={placeholder}
      />
    </div>
  );
};
